import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import {
  Modal,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Banner,
  OTPInput
} from 'wss3-forge'
import { Mail20Regular } from '@fluentui/react-icons'

const PLACEHOLDER_EMAIL_DOMAIN = '@cfx.webba-creative.com'

type Step = 'email' | 'otp'

interface AddEmailModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
}

export function AddEmailModal({ open, onClose, onSuccess }: AddEmailModalProps) {
  const { user } = useAuth()
  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const resetState = () => {
    setStep('email')
    setEmail('')
    setOtpCode('')
    setError(null)
    setLoading(false)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (!email || !email.includes('@')) {
        throw new Error('Please enter a valid email address')
      }

      if (email.includes(PLACEHOLDER_EMAIL_DOMAIN)) {
        throw new Error('Please enter your real email address')
      }

      const { data, error: sendError } = await supabase.functions.invoke('update-user-email', {
        body: { action: 'send_otp', new_email: email }
      })

      if (sendError) {
        throw new Error(sendError.message || 'Failed to send verification code')
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      setStep('otp')
    } catch (err: any) {
      console.error('Send OTP error:', err)
      setError(err.message || 'Failed to send verification code')
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      if (!otpCode || otpCode.length !== 6) {
        throw new Error('Please enter the 6-digit verification code')
      }

      const { data, error: verifyError } = await supabase.functions.invoke('update-user-email', {
        body: { action: 'verify_otp', otp_code: otpCode }
      })

      if (verifyError) {
        throw new Error(verifyError.message || 'Failed to verify code')
      }

      if (data?.error) {
        throw new Error(data.error)
      }

      await supabase.auth.refreshSession()
      resetState()
      onSuccess()
    } catch (err: any) {
      console.error('Verify OTP error:', err)
      setError(err.message || 'Failed to verify code')
    } finally {
      setLoading(false)
    }
  }

  if (!user) return null

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Email Address"
    >
      <VStack gap="md">
        {step === 'email' ? (
          <>
            <Text>
              Add your email address to secure your account and enable password recovery.
            </Text>

            {error && (
              <Banner variant="error" title="Error">
                {error}
              </Banner>
            )}

            <form onSubmit={handleSendOtp} style={{ width: '100%' }}>
              <VStack gap="md">
                <Input
                  label="Email Address"
                  type="email"
                  placeholder="you@example.com"
                  icon={<Mail20Regular />}
                  value={email}
                  onChange={(value) => setEmail(value as string)}
                  required
                />

                <Text size="xs" color="muted">
                  We'll send a verification code to this email address.
                </Text>

                <HStack gap="sm" style={{ width: '100%' }}>
                  <Button
                    type="button"
                    variant="secondary"
                    fullWidth
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    fullWidth
                    loading={loading}
                  >
                    Send Code
                  </Button>
                </HStack>
              </VStack>
            </form>
          </>
        ) : (
          <>
            <Text>
              We've sent a 6-digit verification code to <strong>{email}</strong>.
            </Text>

            {error && (
              <Banner variant="error" title="Error">
                {error}
              </Banner>
            )}

            <form onSubmit={handleVerifyOtp} style={{ width: '100%' }}>
              <VStack gap="lg">
                <VStack gap="xs" style={{ alignItems: 'center' }}>
                  <Text size="sm" color="muted">Enter verification code</Text>
                  <OTPInput
                    length={6}
                    value={otpCode}
                    onChange={setOtpCode}
                    onComplete={() => {}}
                  />
                </VStack>

                <Button
                  type="submit"
                  fullWidth
                  loading={loading}
                  disabled={otpCode.length !== 6}
                >
                  Verify Email
                </Button>

                <HStack gap="sm" style={{ width: '100%' }}>
                  <Button
                    type="button"
                    variant="secondary"
                    fullWidth
                    onClick={() => {
                      setStep('email')
                      setOtpCode('')
                      setError(null)
                    }}
                  >
                    Change Email
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    fullWidth
                    loading={loading}
                    onClick={async () => {
                      setError(null)
                      setLoading(true)
                      try {
                        const { data, error: resendError } = await supabase.functions.invoke('update-user-email', {
                          body: { action: 'send_otp', new_email: email }
                        })
                        if (resendError || data?.error) {
                          throw new Error(data?.error || resendError?.message || 'Failed to resend code')
                        }
                      } catch (err: any) {
                        setError(err.message)
                      } finally {
                        setLoading(false)
                      }
                    }}
                  >
                    Resend Code
                  </Button>
                </HStack>

                <Text size="xs" color="muted" style={{ textAlign: 'center' }}>
                  The code expires in 10 minutes.
                </Text>
              </VStack>
            </form>
          </>
        )}
      </VStack>
    </Modal>
  )
}
